import { useEffect, useState } from "react";
import { browserStorage } from "~/libs/localforage";
import { initialState, FileSettingsContext, FileSettingsState, SearchFilters } from "./file-settings-context";
import type { FC, ReactNode } from "react";
import { usePermissionFilters } from "~/hooks/use-permission-filters";
import { useMeCache } from "~/queries/useMe";
interface FileSettingsProviderProps {
  children?: ReactNode;
}
export const FileSettingsProvider: FC<FileSettingsProviderProps> = props => {
  const {
    children
  } = props;
  const {
    data: me
  } = useMeCache();
  const [state, setState] = useState<FileSettingsState>(initialState);
  const permissions = usePermissionFilters();
  useEffect(() => {
    browserStorage.fileSettings.get().then(r => {
      if (r) {
        const parsedState = JSON.parse(r) as FileSettingsState;
        if (!permissions.VIEW_ALL_ASSIGNEE_TASKS) {
          setState({
            ...parsedState,
            fileByAssigneeSearch: {
              ...parsedState.fileByAssigneeSearch,
              assignees: me?._id ? [me?._id] : []
            } as FileSettingsState["fileByAssigneeSearch"]
          });
        } else {
          setState(parsedState);
        }
      } else {
        if (!permissions.VIEW_ALL_ASSIGNEE_TASKS) {
          setState({
            ...initialState,
            fileByAssigneeSearch: {
              ...initialState.fileByAssigneeSearch,
              assignees: me?._id ?? []
            } as FileSettingsState["fileByAssigneeSearch"]
          });
        }
      }
    }).catch(err => console.log({
      err
    }));
  }, [me, permissions]);
  const handleHideProcessModelOpen = async () => {
    const newState = {
      ...state,
      hideProcess: {
        ...state.hideProcess,
        isModelOpen: true
      }
    };
    await handleStateChange(newState);
  };
  const handleHideProcessModelClose = async () => {
    const newState = {
      ...state,
      hideProcess: {
        ...state.hideProcess,
        isModelOpen: false
      }
    };
    await handleStateChange(newState);
  };
  const handleHideProcess = async (processId: string) => {
    const vals = state.hideProcess?.selectedProcesses ?? [];
    const newState = {
      ...state,
      hideProcess: {
        ...state.hideProcess,
        selectedProcesses: [...vals, processId]
      }
    };
    await handleStateChange(newState);
  };
  const handleUnhideProcess = async (processId: string) => {
    const vals = state.hideProcess?.selectedProcesses ?? [];
    if (vals.length === 0) return;
    const newState = {
      ...state,
      hideProcess: {
        ...state.hideProcess,
        selectedProcesses: vals.filter(v => v !== processId)
      }
    };
    await handleStateChange(newState);
  };
  const handleFileSearchOpen = async () => {
    const newState = {
      ...state,
      fileSearch: {
        ...state.fileSearch,
        isModelOpen: true
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleFileSearchClose = async () => {
    const newState = {
      ...state,
      fileSearch: {
        ...state.fileSearch,
        isModelOpen: false
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleUpdateSearch = async (added: SearchFilters) => {
    const newState = {
      ...state,
      fileSearch: {
        ...state.fileSearch,
        [added.field]: added.search
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleClearSearch = async () => {
    const newState = {
      ...state,
      fileSearch: {
        ...state.fileSearch,
        assignees: [],
        clients: [],
        agents: [],
        creators: [],
        orderStatuses: [],
        paymentStatuses: [],
        files: [""],
        invoices: [""]
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleFileHistorySearchOpen = async () => {
    const newState = {
      ...state,
      fileHistorySearch: {
        ...state.fileHistorySearch,
        isModelOpen: true
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleFileHistorySearchClose = async () => {
    const newState = {
      ...state,
      fileHistorySearch: {
        ...state.fileHistorySearch,
        isModelOpen: false
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleUpdateFileHistorySearch = async (added: SearchFilters) => {
    const newState = {
      ...state,
      fileHistorySearch: {
        ...state.fileHistorySearch,
        [added.field]: added.search
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleClearFileHistorySearch = async () => {
    const newState = {
      ...state,
      fileHistorySearch: {
        ...state.fileSearch,
        assignees: [],
        clients: [],
        agents: [],
        creators: [],
        orderStatuses: [],
        paymentStatuses: [],
        files: [""],
        invoices: [""]
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleFileByAssigneeSearchOpen = async () => {
    const newState = {
      ...state,
      fileByAssigneeSearch: {
        ...state.fileByAssigneeSearch,
        isModelOpen: true
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleFileByAssigneeSearchClose = async () => {
    const newState = {
      ...state,
      fileByAssigneeSearch: {
        ...state.fileByAssigneeSearch,
        isModelOpen: false
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleUpdateFileByAssigneeSearch = async (added: SearchFilters) => {
    const newState = {
      ...state,
      fileByAssigneeSearch: {
        ...state.fileByAssigneeSearch,
        [added.field]: added.search
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleClearFileByAssigneeSearch = async () => {
    const newState = {
      ...state,
      fileByAssigneeSearch: {
        ...state.fileByAssigneeSearch,
        ...(permissions.VIEW_ALL_ASSIGNEE_TASKS && {
          assignees: []
        }),
        clients: [],
        agents: [],
        creators: [],
        orderStatuses: [],
        paymentStatuses: [],
        files: [""],
        invoices: [""]
      }
    } as unknown as FileSettingsState;
    await handleStateChange(newState);
  };
  const handleStateChange = async (state: FileSettingsState) => {
    await browserStorage.fileSettings.set(JSON.stringify(state));
    setState(state);
  };
  return <FileSettingsContext.Provider value={{
    ...state,
    handleHideProcessModelOpen,
    handleHideProcessModelClose,
    handleHideProcess,
    handleUnhideProcess,
    handleFileSearchOpen,
    handleFileSearchClose,
    handleUpdateSearch,
    handleClearSearch,
    handleFileHistorySearchOpen,
    handleFileHistorySearchClose,
    handleUpdateFileHistorySearch,
    handleClearFileHistorySearch,
    handleFileByAssigneeSearchOpen,
    handleFileByAssigneeSearchClose,
    handleUpdateFileByAssigneeSearch,
    handleClearFileByAssigneeSearch
  }} data-sentry-element="unknown" data-sentry-component="FileSettingsProvider" data-sentry-source-file="file-settings-provider.tsx">
			{children}
		</FileSettingsContext.Provider>;
};